import type { AxiosRequestConfig } from 'axios'
import config from 'const/config'
import apiEndpoints from 'const/endPoint'
import {
  getDeactiveOnlineUserParams,
  getHeaderParams
} from 'helpers/requestParams'
import { userAccountInfo } from 'helpers/user'
import type {
  AddOnlineUserRequest,
  ChildAccountPrivilegeRequest,
  GetUserAvailabilityPayload,
  OnlineUserListResponse,
  ResetOnlineUserPasswordRequest,
  ResetOnlineUserPasswordResponse,
  UpdateUserIdRequest,
  UpdateUserPriviliges
} from 'redux/onlineUser/typings'
import apiRequestor from 'services/apiRequestor'
import type {
  NewUserInfo,
  OnlineUserDetailResponse,
  OnlineUserListRequest
} from 'typings/onlineUser'

const { APIGEE_CLIENT_ID } = config

interface ToggleMfaRequest {
  accessToken: string
  userId: string
  accountId: string
  newIsMfaEnabledState: boolean
}

/**
 * Request body for adding an online user. POST /commercial/identity/online-user
 */
export type PostOnlineUsersRequest = Pick<
  AddOnlineUserRequest,
  'userInfo' | 'userPrivilegeInfo' | 'userAdminId'
>

const onlineUserAPI = {
  getOnlineUserList: async (
    payload: OnlineUserListRequest
  ): Promise<OnlineUserListResponse> => {
    const { accessToken, accountID } = payload

    const headerParams = getHeaderParams(accessToken, 'GET')

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.onlineUserList,
      method: 'GET',
      headers: { ...headerParams },
      params: {
        client_id: APIGEE_CLIENT_ID,
        'cipher.accountId': accountID
      }
    }

    const response = await apiRequestor<OnlineUserListResponse>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  getOnlineUserDetail: async (
    payload: OnlineUserListRequest
  ): Promise<OnlineUserDetailResponse> => {
    const { accessToken, userId } = payload

    const headerParams = getHeaderParams(accessToken, 'GET')

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.onlineUserDetail,
      method: 'GET',
      headers: { ...headerParams },
      params: {
        client_id: APIGEE_CLIENT_ID,
        userId
      }
    }

    const response = await apiRequestor<OnlineUserDetailResponse>({
      requestConfig,
      hasAccountInfo: false
    })
    return response.data
  },
  toggleMfa: async (payload: ToggleMfaRequest): Promise<unknown> => {
    const { accessToken, accountId, newIsMfaEnabledState, userId } = payload

    const { accountIdType } = userAccountInfo

    const headerParams = getHeaderParams(accessToken, 'PUT', true)

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.onlineUserMfaToggle,
      method: 'PUT',
      headers: { ...headerParams },
      params: {
        client_id: APIGEE_CLIENT_ID
      },
      data: {
        userId,
        'cipher.accountId': accountId,
        accountIdType,
        overrideEnabled: newIsMfaEnabledState
      }
    }

    const response = await apiRequestor<unknown>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  deactivateOnlineUser: async (
    payload: OnlineUserListRequest
  ): Promise<unknown> => {
    const { accessToken } = payload

    const headerParams = getHeaderParams(accessToken)

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.onlineUserDetail,
      method: 'PUT',
      headers: { ...headerParams },
      params: {
        client_id: APIGEE_CLIENT_ID
      },
      data: getDeactiveOnlineUserParams(payload)
    }

    const response = await apiRequestor<unknown>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  updateOnlineUserPrivilege: async (
    payload: UpdateUserPriviliges
  ): Promise<unknown> => {
    const { accessToken, userId, userPrivilegeInfo } = payload

    const headerParams = getHeaderParams(accessToken)

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.onlineUserPrivilege,
      method: 'PUT',
      headers: { ...headerParams },
      params: {
        client_id: APIGEE_CLIENT_ID
      },
      data: {
        userId,
        userPrivilegeInfo
      }
    }

    const response = await apiRequestor<unknown>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  addOnlineUser: async (
    payload: AddOnlineUserRequest
  ): Promise<NewUserInfo> => {
    const { accessToken, userInfo, userPrivilegeInfo, userAdminId } = payload

    const headerParams = getHeaderParams(accessToken)

    const data: PostOnlineUsersRequest = {
      userInfo,
      userPrivilegeInfo,
      userAdminId
    }

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.onlineUserDetail,
      method: 'POST',
      headers: { ...headerParams },
      params: {
        client_id: APIGEE_CLIENT_ID
      },
      data
    }

    const response = await apiRequestor<NewUserInfo>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  resetOnlineUserPassword: async (
    payload: ResetOnlineUserPasswordRequest
  ): Promise<ResetOnlineUserPasswordResponse> => {
    const { accessToken, userId, temporaryPassword } = payload

    const headerParams = getHeaderParams(accessToken)

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.onlineUserPassword,
      method: 'POST',
      headers: { ...headerParams },
      params: {
        client_id: APIGEE_CLIENT_ID,
        userId
      },
      data: {
        temporaryPassword
      }
    }

    const response = await apiRequestor<ResetOnlineUserPasswordResponse>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  changeUserId: async (payload: UpdateUserIdRequest): Promise<unknown> => {
    const { accessToken, newUserId, userId } = payload

    const headerParams = getHeaderParams(accessToken)

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.updateUserId,
      method: 'PUT',
      headers: { ...headerParams },
      params: {
        client_id: APIGEE_CLIENT_ID
      },
      data: {
        newUserId,
        userId
      }
    }

    const response = await apiRequestor<unknown>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  updateAccountPrivilege: async (
    payload: ChildAccountPrivilegeRequest
  ): Promise<unknown> => {
    const { accessToken, childAccountNumbers, accountId, userPrivilegeInfo } =
      payload

    const headerParams = getHeaderParams(accessToken)

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.childAccountPrivilege,
      method: 'PUT',
      headers: { ...headerParams },
      params: {
        client_id: APIGEE_CLIENT_ID
      },
      data: {
        'cipher.accountId': accountId,
        accountUniqueIdentifiers: childAccountNumbers,
        userPrivilegeInfo
      }
    }

    const response = await apiRequestor<unknown>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  checkUserAvailability: async (
    payload: GetUserAvailabilityPayload
  ): Promise<unknown> => {
    const { accessToken, userId, skipAuthorization } = payload

    const headerParams = getHeaderParams(accessToken, 'GET', true)

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.userAvailability,
      method: 'GET',
      headers: { ...headerParams },
      params: {
        client_id: APIGEE_CLIENT_ID,
        userId,
        skipAuthorization
      }
    }

    const response = await apiRequestor<unknown>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  }
}

export default onlineUserAPI
